import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { OktaAuthService } from 'src/app/core/services/http/okta-auth.service';
import { engineTypes, rawModes, scheduleTypes, watermarkKeyDatatype } from '../../constants/constants';
import { TenantIngestionJob } from '../../models/tenant-ingestion-job';
import { TenantSchedule } from '../../models/tenant-schedule';
import { OktaUser } from '../../models/okta-user';
import { areEqual, cronExpressionsAreEqual } from '../../utils/string.utils';
import { JobModelVM } from './ingestion-jobs-list.component';
import { Observable, concatMap } from 'rxjs';
import { SaveResponse } from 'src/app/core/models/save-response';
import { TenantScheduleService } from 'src/app/core/services/application/tenant-schedule.service';
import { TenantIngestionJobService } from 'src/app/core/services/application/tenant-ingestion-job.service';
import { numberAsStringValidator } from '../../validators/number-string-validator';
import { isBatchedRawMode, isCsaOrCdc } from '../../utils/raw-mode.utils';
import { TenantIngestionPriorityType } from 'src/app/core/models/tenant-ingestion-priority-type';
import { CronService } from '../cron-editor/cron.service';

@Injectable({
    providedIn: 'root'
})
export class IngestionJobsListService {
    scheduleTypes = scheduleTypes;
    rawModes = rawModes;
    oktaUser: OktaUser;
    isWatermarkValueChange = false;
    ingestWatermarkUpdates: number[] = [];

    constructor(
        private fb: FormBuilder,
        private tenantScheduleService: TenantScheduleService,
        private ingestionJobService: TenantIngestionJobService,
        private cronService: CronService,
        oktaAuth?: OktaAuthService
    ) {
        if (oktaAuth) {
            oktaAuth.getUser().then(user => (this.oktaUser = user));
        }
    }

    public mapToJobModel(
        updatedForms: FormGroup<any>[],
        tenantId: string,
        sourceId: number,
        isFileSourceType = false
    ): TenantIngestionJob[] {
        return updatedForms.map(form => {
            const originalJob: TenantIngestionJob = form.value.originalJob;
            const entity = form.value.entity;
            const scheduleId = form.value.schedule.scheduleId;
            const raw_mode = form.value.rawMode;
            const useLegacyEngine = form.value.useLegacyEngine;
            const scheduleType = form.value.schedule.scheduleType;
            const customNotebook = form.value.customNotebook;
            const customCluster = form.value.customCluster;
            const dateTimeFormat = form.value.dateTimeFormat;
            const customExtractorNotebook = form.value.customExtractorNotebook;
            const copyNullWatermarkValue = form.value.copyNullWatermarkValue;
            const isBatched = isBatchedRawMode(raw_mode);
            const isAppendOrReplace = this.isAppendOrReplace(raw_mode);

            const data = {
                tenantId: tenantId,
                tenantSourceId: sourceId,
                entity: entity ? entity : null,
                rawMode: raw_mode ? raw_mode : null,
                engineType: this.mapLegacyEngineBooleanToEngineType(useLegacyEngine),
                tenantScheduleId: scheduleType === this.scheduleTypes.Existing ? scheduleId : null,
                rawCron: '',
                activeFlag: form.value.activeFlag,
                priority: form.value.priority,
                rawBatchSize: isBatched ? this.parseBatchSize(form.value.rawBatchSize) : null,
                rawWatermarkKey: isBatched || isAppendOrReplace ? (form.value.rawWatermarkKey ?? '') : null,
                rawWatermarkKeyDatatype: isBatched ? form.value.rawWatermarkKeyDatatype : null,
                rawWatermarkValue:
                    isBatched || isAppendOrReplace || (isFileSourceType && raw_mode == rawModes.Full.name)
                        ? (form.controls['rawWatermarkValue'].value ?? '')
                        : null,
                customNotebook: customNotebook ? customNotebook : null,
                customCluster: customCluster ? customCluster : null,
                dateTimeFormat: dateTimeFormat ? dateTimeFormat : null,
                customExtractorNotebook: customExtractorNotebook ? customExtractorNotebook : null,
                copyNullWatermarkValue: copyNullWatermarkValue ? copyNullWatermarkValue : false,
                cdcTable: isCsaOrCdc(raw_mode) ? (form.value.cdcTable ?? null) : null,
                cdcSchema: isCsaOrCdc(raw_mode) ? (form.value.cdcSchema ?? null) : null,
                updatedUser: this.oktaUser.username,
                tenantIngestionId: form.value.ingestionId,
                rawBackfillMinimum:
                    raw_mode === this.rawModes.Backfill.name ? (form.value.backFillMinimum ?? '') : null,
                rawBackfillValue: raw_mode === this.rawModes.Backfill.name ? (form.value.backFillDate ?? '') : null,
                tenantScheduleCron: form.value.schedule.scheduleCron,
                tenantScheduleDescription: form.value.schedule.scheduleDescription,
                tenantSourceEntityId: form.value.tenantSourceEntityId,
                rawNextUtc: originalJob.rawNextUtc,
                rawLastUtc: originalJob.rawLastUtc,
                rawStartedUtc: originalJob.rawStartedUtc,
                notes: originalJob.notes
            } as TenantIngestionJob;

            return data;
        });
    }
    public mapToScheduleRequest(ingestionJob: TenantIngestionJob) {
        const tenantScheduleRequest = <TenantSchedule>{};
        tenantScheduleRequest.tenantId = ingestionJob.tenantId;
        tenantScheduleRequest.description = ingestionJob.tenantScheduleDescription ?? '';

        tenantScheduleRequest.cron = ingestionJob.tenantScheduleCron ?? '';
        tenantScheduleRequest.activeFlag = true;
        tenantScheduleRequest.updatedUser = this.oktaUser.username;

        return tenantScheduleRequest;
    }

    public isEquivalent(form: any, data: any, isFileSourceType = false): boolean {
        const isAppendOrReplace = this.isAppendOrReplace(form.rawMode);

        const result =
            areEqual(form.activeFlag, data.activeFlag) &&
            areEqual(form.priority, data.priority) &&
            areEqual(form.rawMode, data.rawMode) &&
            (isBatchedRawMode(form.rawMode)
                ? areEqual(form.rawBatchSize, data.rawBatchSize.toLocaleString()) &&
                  areEqual(form.rawWatermarkKey, data.rawWatermarkKey)
                : true) &&
            (isAppendOrReplace ? areEqual(form.rawWatermarkKey, data.rawWatermarkKey) : true) &&
            areEqual(form.rawWatermarkKeyDatatype, data.rawWatermarkKeyDatatype) &&
            areEqual(this.mapLegacyEngineBooleanToEngineType(form.useLegacyEngine), data.engineType) &&
            areEqual(form.customNotebook, data.customNotebook) &&
            areEqual(form.customCluster, data.customCluster) &&
            areEqual(form.dateTimeFormat, data.dateTimeFormat) &&
            areEqual(form.copyNullWatermarkValue, data.copyNullWatermarkValue) &&
            areEqual(form.customExtractorNotebook, data.customExtractorNotebook) &&
            areEqual(form.cdcTable, data.cdcTable) &&
            areEqual(form.cdcSchema, data.cdcSchema) &&
            (form.schedule.scheduleType !== scheduleTypes.Existing
                ? cronExpressionsAreEqual(form.schedule.rawCron, data.rawCron)
                : true) &&
            (form.schedule.scheduleType === scheduleTypes.Existing
                ? cronExpressionsAreEqual(form.schedule.scheduleCron, data.tenantScheduleCron) &&
                  areEqual(form.schedule.scheduleId, data.tenantScheduleId) &&
                  areEqual(form.schedule.scheduleDescription, data.tenantScheduleDescription)
                : true) &&
            areEqual(form.schedule.scheduleType, scheduleTypes.Existing) &&
            (form.rawMode === rawModes.Backfill.name
                ? areEqual(form.backFillMinimum, data.rawBackfillMinimum) &&
                  areEqual(form.backFillDate, data.rawBackfillValue)
                : true) &&
            (form.rawMode.includes(rawModes.Incremental.name) ||
            (form.rawMode.includes(rawModes.Full.name) && isFileSourceType) ||
            isAppendOrReplace
                ? areEqual(form.rawWatermarkValue, data.rawWatermarkValue)
                : true);

        return result;
    }

    private isAppendOrReplace(rawMode: string): boolean {
        return rawMode === this.rawModes.Append.name || rawMode === this.rawModes.Replace.name;
    }

    // rawBatchSize comes off the form as a comma-formatted string; parse it to a real
    // number since it now flows back into this.jobs (via updateLocalJobData).
    private parseBatchSize(rawValue: string | number | null | undefined): number | null {
        const sanitized = typeof rawValue === 'string' ? rawValue.replace(/,/g, '') : rawValue;
        if (sanitized === '' || sanitized === null || sanitized === undefined) {
            return null;
        }
        const parsed = Number(sanitized);
        return Number.isNaN(parsed) ? null : parsed;
    }

    public setValidatorsForRawMode(rawMode: string, form: FormGroup, job: TenantIngestionJob) {
        const isAppendOrReplace = this.isAppendOrReplace(rawMode);

        if (rawMode === rawModes.Backfill.name) {
            form.get('backFillDate')?.setValidators([Validators.required]);
        } else {
            form.get('backFillDate')?.clearValidators();
        }

        if (isBatchedRawMode(rawMode)) {
            form.get('rawBatchSize')?.setValidators([Validators.required, numberAsStringValidator, Validators.min(1)]);
            if (isCsaOrCdc(rawMode)) {
                form.controls['rawWatermarkKey'].setValue('_UPDATE_DT');
            } else {
                form.controls['rawWatermarkKey'].setValue(job.rawWatermarkKey);
                form.get('rawWatermarkKey')?.setValidators([Validators.required]);
            }
        } else {
            form.get('rawBatchSize')?.clearValidators();
            form.get('rawWatermarkKey')?.clearValidators();

            // Initialize watermark key for Append/Replace modes (optional)
            if (isAppendOrReplace) {
                form.controls['rawWatermarkKey'].setValue(job.rawWatermarkKey || '');
            }
        }

        if (rawMode === rawModes.Incremental_CDC.name) {
            form.get('cdcTable')?.setValidators([Validators.required]);
            form.get('cdcSchema')?.setValidators([Validators.required]);
        } else {
            form.get('cdcTable')?.clearValidators();
            form.get('cdcSchema')?.clearValidators();
        }

        form.get('backFillDate')?.updateValueAndValidity();
        form.get('rawBatchSize')?.updateValueAndValidity();
        form.get('rawWatermarkKey')?.updateValueAndValidity();
        form.get('rawWatermarkKeyDatatype')?.updateValueAndValidity();
        form.get('cdcTable')?.updateValueAndValidity();
        form.get('cdcSchema')?.updateValueAndValidity();
    }

    public validateUpToOneActiveJobPerMode(jobForms: FormGroup[]): void {
        const modeActiveCountMap = new Map<string, number>();
        jobForms.forEach(f => {
            const active = f.controls['activeFlag'].value === true;
            if (active) {
                const mode = f.controls['rawMode'].value;
                let count = modeActiveCountMap.get(mode);
                count = (count ?? 0) + 1;
                modeActiveCountMap.set(mode, count);
            }
        });
        jobForms.forEach(f => {
            const rawModeControl = f.controls['rawMode'];
            const mode = rawModeControl.value;
            const modeCount = modeActiveCountMap.get(mode) ?? 0;
            const active = f.controls['activeFlag'].value === true;
            const errors = active && modeCount > 1 ? { otherActiveJobsWithSameMode: true } : null;
            rawModeControl.setErrors(errors);
        });
    }

    public checkWatermarkValueUpdate(job: JobModelVM) {
        const formControls = job.form.controls;
        const data = job.data;
        const rawMode = formControls['rawMode'].value;
        const isAppendOrReplace = this.isAppendOrReplace(rawMode);

        if (data.tenantIngestionId && (isBatchedRawMode(rawMode) || isAppendOrReplace)) {
            if (rawMode === rawModes.Backfill.name) {
                if (!formControls['backFillDate'].value && !data.rawBackfillValue) {
                    if (this.ingestWatermarkUpdates.includes(data.tenantIngestionId)) {
                        this.ingestWatermarkUpdates.splice(this.ingestWatermarkUpdates.indexOf(data.tenantIngestionId));
                    }
                    this.isWatermarkValueChange = false;
                } else {
                    const isUpdate = formControls['backFillDate'].value !== data.rawBackfillValue;
                    this.trackIngestWatermarkChange(isUpdate, data.tenantIngestionId);
                }
            }

            if (rawMode.includes(rawModes.Incremental.name) || isAppendOrReplace) {
                if (!formControls['rawWatermarkValue'].value && !data.rawWatermarkValue) {
                    if (this.ingestWatermarkUpdates.includes(data.tenantIngestionId)) {
                        this.ingestWatermarkUpdates.splice(this.ingestWatermarkUpdates.indexOf(data.tenantIngestionId));
                    }
                    this.isWatermarkValueChange = false;
                } else {
                    const isUpdate = formControls['rawWatermarkValue'].value !== data.rawWatermarkValue;
                    this.trackIngestWatermarkChange(isUpdate, data.tenantIngestionId);
                }
            }
        }
    }

    private trackIngestWatermarkChange(isUpdate: boolean, id: number) {
        if (isUpdate) {
            this.isWatermarkValueChange = true;
            if (!this.ingestWatermarkUpdates.includes(id)) {
                this.ingestWatermarkUpdates.push(id);
            }
        } else {
            this.isWatermarkValueChange = false;
            if (this.ingestWatermarkUpdates.includes(id)) {
                this.ingestWatermarkUpdates.splice(this.ingestWatermarkUpdates.indexOf(id));
            }
        }
    }

    public saveJob(ingestionJob: TenantIngestionJob): Observable<string | SaveResponse> {
        const hasNewSchedule = ingestionJob.tenantScheduleCron && !ingestionJob.tenantScheduleId;
        const isExistingJob = ingestionJob.tenantIngestionId !== null;
        if (hasNewSchedule) {
            const tenantScheduleRequest = this.mapToScheduleRequest(ingestionJob);

            return this.tenantScheduleService.saveTenantSchedule(tenantScheduleRequest).pipe(
                concatMap(result => {
                    ingestionJob.tenantScheduleId = result.id;

                    if (isExistingJob) {
                        return this.ingestionJobService.updateTenantIngestionJobs(ingestionJob);
                    }

                    return this.ingestionJobService.saveTenantIngestionJobs(ingestionJob);
                })
            );
        } else {
            if (isExistingJob) {
                return this.ingestionJobService.updateTenantIngestionJobs(ingestionJob);
            }

            return this.ingestionJobService.saveTenantIngestionJobs(ingestionJob);
        }
    }

    public createNewJob(
        tenantId: string,
        sourceId: number,
        entity: string,
        tenantSourceEntityId: number | undefined,
        hasRefKeys: boolean,
        jobModels?: JobModelVM[]
    ) {
        const existingCdcTable = jobModels?.find(jm => jm.data.cdcTable)?.data.cdcTable ?? null;
        const existingCdcSchema = jobModels?.find(jm => jm.data.cdcSchema)?.data.cdcSchema ?? null;
        const newJobVM = this.createJobVM(
            {
                activeFlag: true,
                priority: TenantIngestionPriorityType.Normal,
                customCluster: null,
                customNotebook: null,
                dateTimeFormat: null,
                customExtractorNotebook: null,
                copyNullWatermarkValue: false,
                cdcTable: existingCdcTable,
                cdcSchema: existingCdcSchema,
                newIngestionTempId: this.generateNewIngestionTempId(jobModels),
                rawBackfillMinimum: '10000',
                rawBackfillValue: null,
                rawBatchSize: null,
                rawCron: null,
                rawMode: hasRefKeys ? rawModes.Incremental.name : rawModes.Append.name,
                engineType: engineTypes.IoEngine,
                rawWatermarkKey: '',
                rawWatermarkKeyDatatype: null,
                rawWatermarkKeyDatatypeOptions: [...Object.values(watermarkKeyDatatype)],
                rawWatermarkValue: '',
                tenantIngestionId: null,
                tenantScheduleCron: '',
                tenantScheduleDescription: '',
                tenantScheduleId: null,
                entity: entity,
                tenantSourceEntityId: tenantSourceEntityId,
                tenantId: tenantId,
                tenantSourceId: sourceId
            } as TenantIngestionJob,
            tenantId,
            sourceId,
            entity
        );
        newJobVM.form.markAsDirty();
        return newJobVM;
    }

    private generateNewIngestionTempId(jobModels?: JobModelVM[]): number {
        if (!!jobModels && jobModels.length) {
            const unsavedJobIds: number[] = jobModels
                .filter(job => !!job.data.newIngestionTempId)
                .map(job => job.data.newIngestionTempId!);
            if (unsavedJobIds.length === 1) {
                return unsavedJobIds[0] + 1;
            }
            if (unsavedJobIds.length > 1) {
                const orderedIds: number[] = unsavedJobIds.sort((a: number, b: number) => a - b);
                return orderedIds.pop()! + 1;
            }
        }
        return 1;
    }

    public createJobVM(
        job: TenantIngestionJob,
        tenantId: string,
        sourceId: number,
        entity: string,
        schedules?: TenantSchedule[]
    ): JobModelVM {
        job.tenantId = tenantId;
        job.tenantSourceId = sourceId;

        const form = this.fb.group({
            originalJob: new FormControl(job),
            tenantSourceEntityId: new FormControl(job.tenantSourceEntityId),
            ingestionId: new FormControl(job.tenantIngestionId),
            entity: new FormControl(entity),
            activeFlag: new FormControl(job.activeFlag),
            priority: new FormControl(job.priority),
            rawMode: new FormControl(job.rawMode),
            useLegacyEngine: new FormControl(this.mapEngineTypeToLegacyEngineBoolean(job.engineType)),
            rawBatchSize: new FormControl(job.rawBatchSize?.toLocaleString()),
            rawWatermarkKey: new FormControl(job.rawWatermarkKey),
            rawWatermarkKeyDatatype: new FormControl(job.rawWatermarkKeyDatatype),
            rawWatermarkValue: new FormControl(job.rawWatermarkValue),
            customNotebook: new FormControl(job.customNotebook),
            customCluster: new FormControl(job.customCluster),
            dateTimeFormat: new FormControl(job.dateTimeFormat),
            customExtractorNotebook: new FormControl(job.customExtractorNotebook),
            copyNullWatermarkValue: new FormControl(job.copyNullWatermarkValue),
            cdcTable: new FormControl(job.cdcTable),
            cdcSchema: new FormControl(job.cdcSchema),
            schedule: new FormGroup({
                scheduleId: new FormControl(job.tenantScheduleId),
                rawCron: new FormControl(job.rawCron),
                scheduleDescription: new FormControl(job.tenantScheduleDescription),
                scheduleCron: new FormControl(job.tenantScheduleCron),
                scheduleType: new FormControl(this.scheduleTypes.Existing)
            }),
            backFillMinimum: new FormControl(job.rawBackfillMinimum),
            backFillDate: new FormControl(job.rawBackfillValue)
        });
        this.setValidatorsForRawMode(form.value.rawMode ?? '', form, job);

        const assignedSchedule = schedules?.find(s => s.tenantScheduleId === job.tenantScheduleId);
        return {
            form,
            data: job,
            instantActiveFlag: { value: !!job.activeFlag, disabled: false },
            isSaving: false,
            hasUnsavedChanges: true,
            schedule: assignedSchedule,
            scheduleDescription: assignedSchedule?.cron
                ? this.cronService.translateCronWithUtc(assignedSchedule?.cron)
                : 'No Schedule'
        };
    }

    public revertJobForm(jobVM: JobModelVM) {
        const data = jobVM.data;
        const form = jobVM.form;
        form.controls['activeFlag'].setValue(data.activeFlag);
        form.controls['priority'].setValue(data.priority);
        form.controls['rawMode'].setValue(data.rawMode);
        form.controls['useLegacyEngine'].setValue(this.mapEngineTypeToLegacyEngineBoolean(data.engineType));
        form.controls['backFillMinimum'].setValue(data.rawBackfillMinimum);
        form.controls['rawBatchSize'].setValue(data.rawBatchSize);
        form.controls['backFillDate'].setValue(data.rawBackfillValue);
        form.controls['rawWatermarkKey'].setValue(data.rawWatermarkKey);
        form.controls['rawWatermarkKeyDatatype'].setValue(data.rawWatermarkKeyDatatype);
        form.controls['rawWatermarkValue'].setValue(data.rawWatermarkValue);
        form.controls['customNotebook'].setValue(data.customNotebook);
        form.controls['customCluster'].setValue(data.customCluster);
        form.controls['dateTimeFormat'].setValue(data.dateTimeFormat);
        form.controls['copyNullWatermarkValue'].setValue(data.copyNullWatermarkValue);
        form.controls['customExtractorNotebook'].setValue(data.customExtractorNotebook);
        form.controls['cdcTable'].setValue(data.cdcTable);
        form.controls['cdcSchema'].setValue(data.cdcSchema);
        form.controls['schedule'].get('scheduleId')?.setValue(data.tenantScheduleId);

        form.controls['schedule'].get('scheduleType')?.setValue(this.scheduleTypes.Existing);
        form.controls['schedule'].get('rawCron')?.setValue(data.rawCron);
        form.controls['schedule'].get('scheduleDescription')?.setValue(data.tenantScheduleDescription);
        form.controls['schedule'].get('scheduleCron')?.setValue(data.tenantScheduleCron);
    }

    private mapLegacyEngineBooleanToEngineType(useLegacyEngine: boolean): string {
        return useLegacyEngine ? engineTypes.Legacy : engineTypes.IoEngine;
    }

    private mapEngineTypeToLegacyEngineBoolean(engineType: string): boolean {
        switch (engineType) {
            case engineTypes.Legacy:
                return true;
            case engineTypes.IoEngine:
            default:
                return false;
        }
    }
}
